import React, { type JSX } from 'react';
import { type SelectListItem } from '../../typings/viewModels/selectListItemViewModel';
import Button from '../Button/Button';
import TabbedSelect from '../TabbedSelect/TabbedSelect';
import useTheme from '../../contexts/useTheme';

export type ToggleSelectProps<T> = {
  value: SelectListItem<T>;
  options: SelectListItem<T>[];
  onChange?: (option: SelectListItem<T>) => void;
  isDisabled?: boolean;
  style?: ToggleSelectStyle;
  slim?: boolean;
};

export type ToggleSelectStyle = 'menu' | 'bubble' | 'rounded-square';

function ToggleSelect<T extends string | number>({
  options = [],
  value,
  onChange,
  isDisabled = false,
  style,
  slim = false,
}: ToggleSelectProps<T>): JSX.Element {
  const theme = useTheme();
  style = style ?? theme.toggleSelectStyle;

  // FIXME: We added styles and functionality to TabbedSelect that should also exist on ToggleSelect. They should use the same core component.
  if (style !== 'menu') {
    return (
      <TabbedSelect
        value={value}
        options={options}
        onChange={onChange}
        isDisabled={isDisabled}
        style={style}
      />
    );
  }

  return (
    <div className={`toggle-select ${slim ? 'toggle-select--slim' : ''}`}>
      {options.map((option) => (
        <Button
          key={String(option.value)}
          className={'toggle-select__option'}
          styles={[
            'menu',
            value?.value === option.value ? 'menu-selected' : undefined,
            slim ? 'slim' : undefined,
          ]}
          onClick={() => onChange?.(option)}
          disabled={isDisabled}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}

export default ToggleSelect;
